import React, { useEffect, useState, useMemo } from 'react';
import {
  Newspaper, RefreshCw, ExternalLink, Zap, TrendingUp, TrendingDown,
  Minus, Eye, BookOpen, Star, AlertTriangle, BarChart2, Clock,
  ChevronRight, Sparkles, Play, X, Filter, Globe,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useNewsData, NewsAnalysisState } from '../hooks/useNewsData';
import { NewsItem, NewsImpactEntry, NewsTrafficCrossSignal, Holding, WatchlistItem, MacroState, UserProfile, MembershipTier } from '../types';
import { auth } from '../firebase';
import { useTierGate } from '../hooks/useTierGate';
import { TrafficCrossSignalRadar } from './TrafficCrossSignalRadar';

// ─── helpers ─────────────────────────────────────────────────────────────────

function timeAgo(iso: string, lang: string): string {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return lang === 'ko' ? '방금 전' : 'just now';
  if (diff < 3600) return lang === 'ko' ? `${Math.floor(diff / 60)}분 전` : `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return lang === 'ko' ? `${Math.floor(diff / 3600)}시간 전` : `${Math.floor(diff / 3600)}h ago`;
  return lang === 'ko' ? `${Math.floor(diff / 86400)}일 전` : `${Math.floor(diff / 86400)}d ago`;
}

const URGENCY_STYLES = {
  breaking: 'bg-red-500/20 text-red-400 border border-red-500/30',
  major: 'bg-amber-500/15 text-amber-400 border border-amber-500/25',
  normal: 'bg-white/5 text-white/40 border border-white/10',
};

const URGENCY_LABELS: Record<string, Record<string, string>> = {
  breaking: { ko: '속보', en: 'BREAKING' },
  major: { ko: '주요', en: 'MAJOR' },
  normal: { ko: '일반', en: 'NEWS' },
};

const CATEGORY_LABELS: Record<string, Record<string, string>> = {
  earnings: { ko: '실적', en: 'EARNINGS' },
  macro: { ko: '거시경제', en: 'MACRO' },
  geopolitical: { ko: '지정학', en: 'GEO' },
  sector: { ko: '섹터', en: 'SECTOR' },
  general: { ko: '일반', en: 'GENERAL' },
};

const DIRECTION_CONFIG = {
  bullish: {
    icon: TrendingUp,
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10 border-emerald-500/20',
    label: { ko: '상승', en: 'BULLISH' },
  },
  bearish: {
    icon: TrendingDown,
    color: 'text-red-400',
    bg: 'bg-red-500/10 border-red-500/20',
    label: { ko: '하락', en: 'BEARISH' },
  },
  neutral: {
    icon: Minus,
    color: 'text-white/50',
    bg: 'bg-white/5 border-white/10',
    label: { ko: '중립', en: 'NEUTRAL' },
  },
};

const ACTION_STYLES: Record<string, string> = {
  BUY: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  ADD: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  SELL: 'bg-red-500/20 text-red-400 border-red-500/30',
  EXIT: 'bg-red-500/20 text-red-400 border-red-500/30',
  REDUCE: 'bg-amber-500/15 text-amber-400 border-amber-500/25',
  WATCH: 'bg-blue-500/15 text-blue-400 border-blue-500/25',
  HOLD: 'bg-white/5 text-white/40 border-white/10',
};

const MARKET_THEMES = {
  US: {
    accent: 'text-blue-400',
    borderAccent: 'border-blue-500/30',
    bgAccent: 'bg-blue-500/10',
    gradient: 'from-blue-600 to-indigo-500 hover:from-blue-500 hover:to-indigo-400',
    iconGradient: 'from-blue-500 to-indigo-600',
    glow: 'shadow-blue-500/20',
    spinner: 'border-blue-500/40 border-t-blue-500',
    code: 'prose-code:text-blue-400 prose-code:bg-blue-500/10 prose-blockquote:border-l-blue-500',
    title: { ko: '미국 증시 속보 · 포트폴리오 영향 분석', en: 'US Market Breaking News & Impact' }
  },
  KR: {
    accent: 'text-emerald-400',
    borderAccent: 'border-emerald-500/30',
    bgAccent: 'bg-emerald-500/10',
    gradient: 'from-emerald-600 to-teal-500 hover:from-emerald-500 hover:to-teal-400',
    iconGradient: 'from-emerald-500 to-teal-600',
    glow: 'shadow-emerald-500/20',
    spinner: 'border-emerald-500/40 border-t-emerald-500',
    code: 'prose-code:text-emerald-400 prose-code:bg-emerald-500/10 prose-blockquote:border-l-emerald-500',
    title: { ko: '한국 증시 속보 · 포트폴리오 영향 분석', en: 'Korea Market Breaking News & Impact' }
  },
  ALL: {
    accent: 'text-purple-400',
    borderAccent: 'border-purple-500/30',
    bgAccent: 'bg-purple-500/10',
    gradient: 'from-purple-600 to-pink-500 hover:from-purple-500 hover:to-pink-400',
    iconGradient: 'from-purple-500 to-pink-600',
    glow: 'shadow-purple-500/20',
    spinner: 'border-purple-500/40 border-t-purple-500',
    code: 'prose-code:text-purple-400 prose-code:bg-purple-500/10 prose-blockquote:border-l-purple-500',
    title: { ko: '글로벌 증시 속보 · 포트폴리오 영향 분석', en: 'Global Market Breaking News & Impact' }
  }
};

const getTextFromNode = (node: any): string => {
  if (!node) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(getTextFromNode).join('');
  if (React.isValidElement<any>(node)) {
    return getTextFromNode(node.props.children);
  }
  return '';
};

const customMarkdownComponents: any = {
  h2: ({ node, children, ...props }: any) => (
    <h2 className="text-[14px] font-black text-white mt-6 mb-3 pb-1.5 border-b border-white/10 flex items-center gap-2" {...props}>
      {children}
    </h2>
  ),
  h3: ({ node, children, ...props }: any) => (
    <h3 className="text-[13px] font-bold text-white/90 mt-4 mb-2" {...props}>
      {children}
    </h3>
  ),
  p: ({ node, children, ...props }: any) => (
    <p className="text-[12px] leading-relaxed text-white/70 mb-3.5" {...props}>
      {children}
    </p>
  ),
  strong: ({ node, children, ...props }: any) => (
    <strong className="text-white font-black text-[12px]" {...props}>
      {children}
    </strong>
  ),
  li: ({ node, children, ...props }: any) => {
    const childrenArray = React.Children.toArray(children);
    let isBuy = false;
    let isRisk = false;
    let isMonitor = false;

    const checkText = (text: string) => {
      const trimmed = text.trim();
      if (trimmed.includes('매수 고려') || trimmed.toLowerCase().includes('buy consideration')) isBuy = true;
      if (trimmed.includes('리스크 관리') || trimmed.toLowerCase().includes('risk management') || trimmed.includes('리스크')) isRisk = true;
      if (trimmed.includes('모니터링') || trimmed.toLowerCase().includes('monitoring') || trimmed.includes('주시')) isMonitor = true;
    };

    childrenArray.forEach(child => {
      if (typeof child === 'string') {
        checkText(child);
      } else if (React.isValidElement(child)) {
        const grandChildren = (child as any).props?.children;
        if (typeof grandChildren === 'string') {
          checkText(grandChildren);
        } else if (Array.isArray(grandChildren)) {
          grandChildren.forEach(gc => {
            if (typeof gc === 'string') checkText(gc);
          });
        }
      }
    });

    if (isBuy) {
      return (
        <li className="list-none my-3.5 p-4 rounded-xl border bg-emerald-500/5 border-emerald-500/20 text-emerald-300 flex items-start gap-3 shadow-md shadow-emerald-950/20 transition-all hover:bg-emerald-500/10">
          <span className="text-lg flex-shrink-0 leading-none">📈</span>
          <div className="text-[12px] leading-relaxed text-emerald-200/90 flex-1">{children}</div>
        </li>
      );
    }
    if (isRisk) {
      return (
        <li className="list-none my-3.5 p-4 rounded-xl border bg-red-500/5 border-red-500/20 text-red-300 flex items-start gap-3 shadow-md shadow-red-950/20 transition-all hover:bg-red-500/10">
          <span className="text-lg flex-shrink-0 leading-none">⚠️</span>
          <div className="text-[12px] leading-relaxed text-red-200/90 flex-1">{children}</div>
        </li>
      );
    }
    if (isMonitor) {
      return (
        <li className="list-none my-3.5 p-4 rounded-xl border bg-amber-500/5 border-amber-500/20 text-amber-300 flex items-start gap-3 shadow-md shadow-amber-950/20 transition-all hover:bg-amber-500/10">
          <span className="text-lg flex-shrink-0 leading-none">🔍</span>
          <div className="text-[12px] leading-relaxed text-amber-200/90 flex-1">{children}</div>
        </li>
      );
    }

    return (
      <li className="my-1.5 list-disc list-inside text-white/70 text-[12px] leading-relaxed" {...props}>
        {children}
      </li>
    );
  },
  table: ({ node, children, ...props }: any) => {
    const headers: string[] = [];
    const rows: string[][] = [];

    React.Children.forEach(children, (childSection) => {
      if (!React.isValidElement<any>(childSection)) return;

      const sectionType = childSection.type;
      const sectionName = typeof sectionType === 'string' ? sectionType : (sectionType as any).name || '';

      if (sectionName === 'thead') {
        React.Children.forEach(childSection.props.children, (tr) => {
          if (!React.isValidElement<any>(tr)) return;
          React.Children.forEach(tr.props.children, (th) => {
            headers.push(getTextFromNode(th).trim());
          });
        });
      } else if (sectionName === 'tbody') {
        React.Children.forEach(childSection.props.children, (tr) => {
          if (!React.isValidElement<any>(tr)) return;
          const rowCells: string[] = [];
          React.Children.forEach(tr.props.children, (td) => {
            rowCells.push(getTextFromNode(td).trim());
          });
          if (rowCells.length > 0) {
            rows.push(rowCells);
          }
        });
      } else if (sectionName === 'tr') {
        const cells: string[] = [];
        let isHeader = false;
        React.Children.forEach(childSection.props.children, (cell) => {
          if (!React.isValidElement<any>(cell)) return;
          const cellName = typeof cell.type === 'string' ? cell.type : (cell.type as any).name || '';
          if (cellName === 'th') isHeader = true;
          cells.push(getTextFromNode(cell).trim());
        });
        if (isHeader) {
          headers.push(...cells);
        } else if (cells.length > 0) {
          rows.push(cells);
        }
      }
    });

    const isScenarioTable = headers.some(h => h.includes('시나리오') || h.includes('Scenario')) ||
                            rows.some(r => r[0] && (r[0].includes('시나리오') || r[0].includes('Case') || r[0].includes('강세') || r[0].includes('약세') || r[0].includes('neutral')));

    if (isScenarioTable) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-4">
          {rows.map((row, idx) => {
            const scenarioName = row[0] || `Scenario ${idx + 1}`;
            const probability = row[1] || '';
            const actionText = row[2] || '';

            let cardBg = 'bg-white/[0.02] border-white/5 hover:border-white/10 text-white';
            let badgeColor = 'bg-white/5 text-white/50 border border-white/10';
            let titlePrefix = '📊';

            if (scenarioName.includes('강세') || scenarioName.toLowerCase().includes('bull')) {
              cardBg = 'bg-emerald-500/[0.03] border-emerald-500/10 hover:border-emerald-500/20 text-emerald-100';
              badgeColor = 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20';
              titlePrefix = '🚀';
            } else if (scenarioName.includes('약세') || scenarioName.toLowerCase().includes('bear')) {
              cardBg = 'bg-red-500/[0.03] border-red-500/10 hover:border-red-500/20 text-red-100';
              badgeColor = 'bg-red-500/10 text-red-400 border border-red-500/20';
              titlePrefix = '📉';
            } else if (scenarioName.includes('중립') || scenarioName.toLowerCase().includes('neutral') || scenarioName.includes('기본')) {
              cardBg = 'bg-blue-500/[0.03] border-blue-500/10 hover:border-blue-500/20 text-blue-100';
              badgeColor = 'bg-blue-500/10 text-blue-400 border border-blue-500/20';
              titlePrefix = '⚖️';
            }

            return (
              <div 
                key={idx} 
                className={`p-4 rounded-2xl border transition-all duration-300 shadow-md ${cardBg}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-[13px] flex items-center gap-1.5">
                    <span>{titlePrefix}</span>
                    {scenarioName}
                  </span>
                  {probability && (
                    <span className={`text-[10px] font-black tracking-wider px-2 py-0.5 rounded-full ${badgeColor}`}>
                      {probability}
                    </span>
                  )}
                </div>
                <p className="text-[12px] text-white/60 leading-relaxed font-normal">
                  {actionText || probability}
                </p>
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <div className="overflow-x-auto my-4 rounded-xl border border-white/5 bg-white/[0.01]">
        <table className="min-w-full divide-y divide-white/5 text-[12px]" {...props}>
          <thead className="bg-white/[0.02]">
            <tr>
              {headers.map((h, i) => (
                <th key={i} className="px-4 py-2 text-left font-bold text-white/50 tracking-wider">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-white/[0.03] bg-transparent">
            {rows.map((row, i) => (
              <tr key={i} className="hover:bg-white/[0.01]">
                {row.map((cell, j) => (
                  <td key={j} className="px-4 py-2 text-white/70 whitespace-pre-wrap">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
};

// ─── Sub-components ───────────────────────────────────────────────────────────

interface NewsCardProps {
  item: NewsItem;
  isSelected: boolean;
  onClick: () => void;
  lang: string;
  analysis?: NewsAnalysisState;
  isHolding?: boolean;
  isWatchlist?: boolean;
  trafficSignal?: NewsTrafficCrossSignal;
}

function NewsCard({ item, isSelected, onClick, lang, analysis, isHolding, isWatchlist, trafficSignal }: NewsCardProps) {
  const hasPrecomputedImpact = (item as any).impactScore !== undefined && (item as any).impactScore !== 0;
  
  const dirConf = hasPrecomputedImpact
    ? ((item as any).impactScore > 0 ? DIRECTION_CONFIG.bullish : (item as any).impactScore < 0 ? DIRECTION_CONFIG.bearish : DIRECTION_CONFIG.neutral)
    : analysis?.impactMap && analysis.impactMap.length > 0
    ? DIRECTION_CONFIG[analysis.impactMap[0].direction]
    : analysis?.impactMap && analysis.impactMap.length === 0 && analysis.analysisText
    ? DIRECTION_CONFIG.neutral
    : null;

  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-4 rounded-2xl transition-all border group relative ${
        isSelected
          ? 'bg-emerald-500/10 border-emerald-500/30 shadow-lg shadow-emerald-500/5'
          : 'bg-white/[0.02] border-white/5 hover:bg-white/[0.05] hover:border-white/10'
      }`}
    >
      {/* Urgency glow for breaking news */}
      {item.urgency === 'breaking' && !isSelected && (
        <span className="absolute top-3 right-3 w-2 h-2 rounded-full bg-red-500 animate-pulse" />
      )}

      <div className="flex items-start gap-3 justify-between">
        <div className="flex flex-col gap-1.5 min-w-0 flex-1">
          {/* Badges row */}
          <div className="flex flex-wrap gap-1.5 items-center">
            <span className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-md ${URGENCY_STYLES[item.urgency]}`}>
              {URGENCY_LABELS[item.urgency][lang] || item.urgency}
            </span>
            <span className="text-[9px] font-bold uppercase tracking-wider text-white/25 px-1.5 py-0.5 rounded-md bg-white/[0.03]">
              {CATEGORY_LABELS[item.category]?.[lang] || item.category}
            </span>
            {/* AI impact score badge */}
            {hasPrecomputedImpact && (
              <span className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-md border flex items-center gap-1 ${
                (item as any).impactScore > 0 ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'
              }`}>
                <Sparkles className="w-2.5 h-2.5 text-purple-400 animate-pulse" />
                {lang === 'ko' ? '영향도' : 'Impact'}: {(item as any).impactScore > 0 ? `+${(item as any).impactScore.toFixed(1)}` : (item as any).impactScore.toFixed(1)}
              </span>
            )}
            {/* AI recommended action badge */}
            {(item as any).recommendedAction && (item as any).recommendedAction !== 'HOLD' && (
              <span className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-md border ${
                ACTION_STYLES[(item as any).recommendedAction] || 'bg-white/5 border-white/10 text-white/50'
              }`}>
                {(item as any).recommendedAction}
              </span>
            )}
            {/* Web Traffic Fact-Check Impact Badge */}
            {trafficSignal && (
              <span
                className={`text-[9px] font-black tracking-wider px-1.5 py-0.5 rounded-md border flex items-center gap-1 ${
                  trafficSignal.signalType === 'CONFIRMED_BREAKOUT'
                    ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                    : trafficSignal.signalType === 'HYPE_TRAP_WARNING'
                    ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                    : 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30'
                }`}
              >
                <Globe className="w-2.5 h-2.5" />
                <span>
                  {trafficSignal.signalType === 'CONFIRMED_BREAKOUT'
                    ? lang === 'ko'
                      ? `트래픽 급증 ${trafficSignal.trafficWowGrowthPct >= 0 ? '+' : ''}${trafficSignal.trafficWowGrowthPct.toFixed(0)}% 🔥`
                      : `Traffic ${trafficSignal.trafficWowGrowthPct >= 0 ? '+' : ''}${trafficSignal.trafficWowGrowthPct.toFixed(0)}% 🔥`
                    : trafficSignal.signalType === 'HYPE_TRAP_WARNING'
                    ? lang === 'ko'
                      ? `트래픽 괴리 ${trafficSignal.trafficWowGrowthPct.toFixed(0)}% ⚠️`
                      : `Traffic Lag ${trafficSignal.trafficWowGrowthPct.toFixed(0)}% ⚠️`
                    : lang === 'ko'
                    ? `웹모멘텀 ${trafficSignal.trafficMomentumScore.toFixed(0)}점`
                    : `Web Score ${trafficSignal.trafficMomentumScore.toFixed(0)}`}
                </span>
              </span>
            )}
          </div>

          {/* Title */}
          <p className={`text-[12px] font-semibold leading-snug line-clamp-2 transition-colors ${
            isSelected ? 'text-white' : 'text-white/70 group-hover:text-white/90'
          }`}>
            {item.title}
          </p>

          {/* AI Strategy Advice Sneak Peek */}
          {(item as any).strategyAdvice && (
            <p className="text-[10px] text-purple-300 leading-snug bg-purple-500/5 border border-purple-500/10 rounded-lg p-1.5 mt-1 font-medium italic flex items-center gap-1.5">
              <Sparkles className="w-3 h-3 text-purple-400 flex-shrink-0" />
              <span>AI 대응: {(item as any).strategyAdvice}</span>
            </p>
          )}

          {/* Meta */}
          <div className="flex items-center gap-2 text-[10px] text-white/25 font-medium mt-0.5">
            <span className="flex items-center gap-1">
              <Clock className="w-2.5 h-2.5" />
              {timeAgo(item.publishedAt, lang)}
            </span>
            <span>·</span>
            <span className="truncate max-w-[100px]">{item.source}</span>
          </div>

          {/* Ticker tags */}
          {item.relatedSymbols.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {item.relatedSymbols.slice(0, 4).map(sym => (
                <span
                  key={sym}
                  className="text-[9px] font-black font-mono text-emerald-400/70 bg-emerald-500/10 border border-emerald-500/15 px-1.5 py-0.5 rounded"
                >
                  ${sym}
                </span>
              ))}
            </div>
          )}
          {(isHolding || isWatchlist) && (
            <div className="flex flex-wrap gap-1 mt-1">
              {isHolding && (
                <span className="text-[9px] font-black font-mono text-purple-400/70 bg-purple-500/10 border border-purple-500/15 px-1.5 py-0.5 rounded">
                  {lang === 'ko' ? '보유' : 'HELD'}
                </span>
              )}
              {isWatchlist && (
                <span className="text-[9px] font-black font-mono text-blue-400/70 bg-blue-500/10 border border-blue-500/15 px-1.5 py-0.5 rounded">
                  {lang === 'ko' ? '관심' : 'WATCH'}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Real-time status / direction badge */}
        <div className="flex flex-col items-center justify-center flex-shrink-0 self-center pl-2">
          {analysis?.isAnalyzing ? (
            <div className="w-3.5 h-3.5 border-2 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin" />
          ) : dirConf ? (
            <span className={`text-[9px] font-black tracking-wider px-2 py-0.5 rounded border ${dirConf.bg} ${dirConf.color}`}>
              {dirConf.label[lang]}
            </span>
          ) : (
            <ChevronRight className={`w-3.5 h-3.5 transition-all ${
              isSelected ? 'text-emerald-400 translate-x-0.5' : 'text-white/20 group-hover:text-white/40'
            }`} />
          )}
        </div>
      </div>
    </button>
  );
}

// ─── Impact Entry Card ────────────────────────────────────────────────────────

interface ImpactCardProps {
  entry: NewsImpactEntry;
  lang: string;
  onAddWatchlist?: (symbol: string) => void;
  onOpenSimulator?: (symbol: string, type: 'holding' | 'watchlist') => void;
}

function ImpactCard({ entry, lang, onAddWatchlist, onOpenSimulator }: ImpactCardProps) {
  const dirConf = DIRECTION_CONFIG[entry.direction];
  const DirIcon = dirConf.icon;
  const absMag = Math.abs(entry.magnitude);
  const magPercent = absMag;

  return (
    <div className={`rounded-2xl border p-4 transition-all ${dirConf.bg}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${
            entry.direction === 'bullish' ? 'bg-emerald-500/20' :
            entry.direction === 'bearish' ? 'bg-red-500/20' : 'bg-white/10'
          }`}>
            <DirIcon className={`w-4 h-4 ${dirConf.color}`} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-black font-mono text-white">{entry.symbol}</span>
              {(entry.isHolding || entry.isWatchlist) && (
                <span className={`text-[8px] font-black uppercase px-1.5 py-0.5 rounded-md ${
                  entry.isHolding
                    ? 'bg-purple-500/20 text-purple-400 border border-purple-500/20'
                    : 'bg-blue-500/15 text-blue-400 border border-blue-500/20'
                }`}>
                  {entry.isHolding ? (lang === 'ko' ? '보유' : 'HELD') : (lang === 'ko' ? '관심' : 'WATCH')}
                </span>
              )}
            </div>
            {entry.name && (
              <p className="text-[10px] text-white/40 font-medium truncate max-w-[140px]">{entry.name}</p>
            )}
          </div>
        </div>

        <div className="flex flex-col items-end gap-1.5">
          <span className={`text-[9px] font-black uppercase tracking-wider px-2 py-1 rounded-lg border ${ACTION_STYLES[entry.recommendedAction] || ACTION_STYLES.HOLD}`}>
            {entry.recommendedAction}
          </span>
          <span className={`text-xs font-black ${dirConf.color}`}>
            {entry.direction === 'bullish' ? '+' : entry.direction === 'bearish' ? '-' : '±'}{absMag}
          </span>
        </div>
      </div>

      {/* Magnitude bar */}
      <div className="mt-3 h-1 bg-white/5 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-700 ${
            entry.direction === 'bullish' ? 'bg-emerald-500' :
            entry.direction === 'bearish' ? 'bg-red-500' : 'bg-white/30'
          }`}
          style={{ width: `${magPercent}%` }}
        />
      </div>

      {/* Rationale */}
      <p className="text-[11px] text-white/50 mt-2.5 leading-relaxed">{entry.rationale}</p>

      {/* Quick actions */}
      {(onAddWatchlist || onOpenSimulator) && (
        <div className="flex gap-1.5 mt-3">
          {!entry.isWatchlist && !entry.isHolding && onAddWatchlist && (
            <button
              onClick={() => onAddWatchlist(entry.symbol)}
              className="flex items-center gap-1 px-2 py-1 rounded-lg bg-white/5 hover:bg-white/10 text-white/50 hover:text-white text-[9px] font-bold uppercase tracking-wider transition-all border border-white/5"
            >
              <Star className="w-2.5 h-2.5" />
              {lang === 'ko' ? '관심종목' : 'Watchlist'}
            </button>
          )}
          {(entry.isHolding || entry.isWatchlist) && onOpenSimulator && (
            <button
              onClick={() => onOpenSimulator(entry.symbol, entry.isHolding ? 'holding' : 'watchlist')}
              className="flex items-center gap-1 px-2 py-1 rounded-lg bg-purple-500/15 hover:bg-purple-500/25 text-purple-400 text-[9px] font-bold uppercase tracking-wider transition-all border border-purple-500/20"
            >
              <BarChart2 className="w-2.5 h-2.5" />
              {lang === 'ko' ? '시뮬레이터' : 'Simulate'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main View ────────────────────────────────────────────────────────────────

interface NewsImpactViewProps {
  holdings: Holding[];
  watchlist: WatchlistItem[];
  macro: MacroState | null;
  market: 'US' | 'KR' | 'ALL';
  language?: string;
  onAddWatchlist?: (symbol: string, name?: string, market?: string, notes?: string) => void;
  onNavigateTo?: (tab: string, symbol?: string) => void;
  userProfile?: UserProfile | null;
  onUpgradeRequest?: (requiredTier: MembershipTier, featureName: string) => void;
}

type CategoryFilter = 'all' | 'earnings' | 'macro' | 'geopolitical' | 'sector';

export function NewsImpactView({
  holdings,
  watchlist,
  macro,
  market,
  language = 'ko',
  onAddWatchlist,
  onNavigateTo,
  userProfile,
  onUpgradeRequest,
}: NewsImpactViewProps) {
  const lang = language;
  const theme = MARKET_THEMES[market] || MARKET_THEMES.ALL;
  const {
    news, isLoadingFeed, feedError, fetchNewsFeed, cachedAt,
    selectedNews, selectNews,
    analyses, analyzeNews, autoAnalyzeNewsList, clearAnalysis,
  } = useNewsData();

  const { tier, checkUsageLimit } = useTierGate(userProfile || null);

  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [urgencyFilter, setUrgencyFilter] = useState<'all' | 'breaking' | 'major'>('all');
  const [scopeFilter, setScopeFilter] = useState<'all' | 'holdings' | 'watchlist'>('all');
  const [viewMode, setViewMode] = useState<'news_feed' | 'traffic_radar'>('news_feed');
  const [trafficSignalsBySymbol, setTrafficSignalsBySymbol] = useState<Record<string, NewsTrafficCrossSignal>>({});

  // Load news feed & background traffic cross signals for fact-check badges
  useEffect(() => {
    fetchNewsFeed(market, lang);
    fetch(`/api/traffic/cross-signals?market=ALL`)
      .then(r => (r.ok ? (r.json() as Promise<{ signals?: NewsTrafficCrossSignal[] }>) : null))
      .then(data => {
        if (data && Array.isArray(data.signals)) {
          const map: Record<string, NewsTrafficCrossSignal> = {};
          for (const sig of data.signals) {
            if (sig.symbol) map[sig.symbol.toUpperCase()] = sig;
          }
          setTrafficSignalsBySymbol(map);
        }
      })
      .catch(() => {});
  }, [market, lang]);

  const filteredNews = useMemo(() => {
    return news.filter(item => {
      if (categoryFilter !== 'all' && item.category !== categoryFilter) return false;
      if (urgencyFilter !== 'all' && item.urgency !== urgencyFilter) return false;
      
      if (scopeFilter === 'holdings') {
        const holdingSymbols = new Set(holdings.map(h => h.symbol.toUpperCase()));
        const hasHolding = item.relatedSymbols && item.relatedSymbols.some(sym => holdingSymbols.has(sym.toUpperCase()));
        if (!hasHolding) return false;
      } else if (scopeFilter === 'watchlist') {
        const watchlistSymbols = new Set(watchlist.map(w => w.symbol.toUpperCase()));
        const hasWatchlist = item.relatedSymbols && item.relatedSymbols.some(sym => watchlistSymbols.has(sym.toUpperCase()));
        if (!hasWatchlist) return false;
      }
      return true;
    });
  }, [news, categoryFilter, urgencyFilter, scopeFilter, holdings, watchlist]);

  const handleSelectNews = (item: NewsItem) => {
    if (selectedNews?.id === item.id) {
      selectNews(null);
    } else {
      selectNews(item);
    }
  };

  const handleAnalyze = () => {
    if (!selectedNews) return;

    // AI 뉴스 분석 한도 체크
    if (!checkUsageLimit('NEWS_ANALYSIS', userProfile?.aiUsageDetails?.NEWS_ANALYSIS || 0)) {
      const tierOrder: MembershipTier[] = ['TIER1', 'TIER2', 'TIER3', 'TIER4', 'TIER5'];
      const currentIndex = tierOrder.indexOf(tier);
      const targetTier: MembershipTier = currentIndex < 4 ? tierOrder[currentIndex + 1] : 'TIER5';
      onUpgradeRequest?.(targetTier, lang === 'ko' ? '뉴스 AI 임팩트 분석' : 'News AI Impact Analysis');
      return;
    }

    analyzeNews(selectedNews, holdings, watchlist, macro, market, lang);
  };

  const handleRefresh = () => {
    fetchNewsFeed(market, lang, true);
    selectNews(null);
    clearAnalysis();
  };

  // Get active analysis data
  const analysis = selectedNews ? analyses[selectedNews.id] : null;
  const isAnalyzing = analysis?.isAnalyzing || false;
  const analysisText = analysis?.analysisText || '';
  const impactMap = analysis?.impactMap || [];

  // Extract summary section from analysis text
  const streamedSummaryText = useMemo(() => {
    if (!analysisText) return '';
    const startIdx = analysisText.indexOf('[NEWS_SUMMARY]');
    if (startIdx === -1) return '';
    let text = analysisText.slice(startIdx + '[NEWS_SUMMARY]'.length);
    const impactIdx = text.indexOf('[IMPACT_MAP]');
    if (impactIdx !== -1) {
      text = text.slice(0, impactIdx);
    }
    const strategyIdx = text.indexOf('[POSITION_STRATEGY]');
    if (strategyIdx !== -1) {
      text = text.slice(0, strategyIdx);
    }
    return text.trim();
  }, [analysisText]);

  // Extract strategy section from analysis text
  const strategyText = useMemo(() => {
    const idx = analysisText.indexOf('[POSITION_STRATEGY]');
    if (idx === -1) return '';
    let text = analysisText.slice(idx + '[POSITION_STRATEGY]'.length);
    // Remove [EOF] marker
    text = text.replace(/\[EOF\]/g, '').trim();
    const mapIdx = text.indexOf('[IMPACT_MAP]');
    if (mapIdx !== -1) text = text.slice(0, mapIdx);
    return text.trim();
  }, [analysisText]);

  const categories: CategoryFilter[] = ['all', 'earnings', 'macro', 'geopolitical', 'sector'];
  const catLabels: Record<CategoryFilter, Record<string, string>> = {
    all: { ko: '전체', en: 'All' },
    earnings: { ko: '실적', en: 'Earnings' },
    macro: { ko: '거시경제', en: 'Macro' },
    geopolitical: { ko: '지정학', en: 'Geo' },
    sector: { ko: '섹터', en: 'Sector' },
  };

  const breakingCount = news.filter(n => n.urgency === 'breaking').length;

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-0 w-full h-full">
      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-6 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-2xl bg-gradient-to-br ${theme.iconGradient} flex items-center justify-center shadow-lg ${theme.glow}`}>
            <Newspaper className="w-5 h-5 text-black" />
          </div>
          <div>
            <h2 className="text-lg font-black text-white">
              {lang === 'ko' ? theme.title.ko : theme.title.en}
            </h2>
            <p className="text-[11px] text-white/30 font-medium">
              {lang === 'ko' ? '뉴스를 선택하시면 실시간 AI 영향 분석 보고서가 노출됩니다' : 'Select a news item to view real-time AI impact report'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
          {/* Sub-mode switcher: Breaking News Feed vs Web Traffic × News Radar */}
          <div className="flex items-center p-1 rounded-xl bg-white/[0.04] border border-white/10">
            <button
              onClick={() => setViewMode('news_feed')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-black transition-all ${
                viewMode === 'news_feed'
                  ? 'bg-white text-black shadow'
                  : 'text-white/50 hover:text-white'
              }`}
            >
              <Newspaper className="w-3.5 h-3.5" />
              <span>{lang === 'ko' ? '실시간 증시 속보' : 'Breaking News'}</span>
            </button>
            <button
              onClick={() => setViewMode('traffic_radar')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-black transition-all ${
                viewMode === 'traffic_radar'
                  ? 'bg-gradient-to-r from-cyan-500 to-emerald-400 text-black shadow'
                  : 'text-cyan-300/80 hover:text-cyan-200'
              }`}
            >
              <Globe className="w-3.5 h-3.5" />
              <span>{lang === 'ko' ? '웹트래픽 × 속보 추천 레이더' : 'Web Traffic × News Radar'}</span>
            </button>
          </div>

          {breakingCount > 0 && (
            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-500/15 border border-red-500/25 text-red-400 text-xs font-black">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
              {lang === 'ko' ? `속보 ${breakingCount}건` : `${breakingCount} Breaking`}
            </span>
          )}
          {cachedAt && (
            <span className="text-[10px] text-white/20 font-medium">
              {lang === 'ko' ? '수집기준' : 'collected at'} {new Date(cachedAt).toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={handleRefresh}
            disabled={isLoadingFeed}
            className="flex items-center gap-1.5 px-3 py-2 bg-white/5 hover:bg-white/10 rounded-xl text-white/60 hover:text-white text-xs font-bold transition-all border border-white/5 disabled:opacity-40"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isLoadingFeed ? 'animate-spin' : ''}`} />
            {lang === 'ko' ? '새로고침' : 'Refresh'}
          </button>
        </div>
      </div>

      {viewMode === 'traffic_radar' ? (
        <TrafficCrossSignalRadar
          market={market}
          lang={lang}
          holdingSymbols={new Set(holdings.map(h => h.symbol.toUpperCase()))}
          watchlistSymbols={new Set(watchlist.map(w => w.symbol.toUpperCase()))}
          onAddWatchlist={onAddWatchlist}
          onSelectNewsTitle={(newsTitle) => {
            const found = news.find(n => n.title === newsTitle || n.title.includes(newsTitle));
            if (found) {
              selectNews(found);
              setViewMode('news_feed');
            }
          }}
        />
      ) : (
      /* ── Split Layout ── */
      <div className="flex gap-5 min-h-0 flex-1 overflow-hidden">
        {/* ── LEFT: News Feed ── */}
        <div className="w-full md:w-[380px] lg:w-[400px] flex-shrink-0 flex flex-col gap-3 h-full min-h-0">
          {/* Filters */}
          <div className="flex flex-col gap-2 flex-shrink-0">
            {/* Category */}
            <div className="flex gap-1.5 flex-wrap">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setCategoryFilter(cat)}
                  className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all ${
                    categoryFilter === cat
                      ? 'bg-white text-black'
                      : 'bg-white/5 text-white/40 hover:text-white/70 border border-white/5'
                  }`}
                >
                  {catLabels[cat][lang]}
                </button>
              ))}
            </div>
            {/* Urgency */}
            <div className="flex gap-1.5">
              {(['all', 'breaking', 'major'] as const).map(u => (
                <button
                  key={u}
                  onClick={() => setUrgencyFilter(u)}
                  className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all ${
                    urgencyFilter === u
                      ? u === 'breaking' ? 'bg-red-500/30 text-red-300 border border-red-500/40'
                        : u === 'major' ? 'bg-amber-500/25 text-amber-300 border border-amber-500/30'
                        : 'bg-white text-black'
                      : 'bg-white/5 text-white/40 hover:text-white/70 border border-white/5'
                  }`}
                >
                  {u === 'all' ? (lang === 'ko' ? '전체' : 'All') :
                   u === 'breaking' ? (lang === 'ko' ? '속보' : 'Breaking') :
                   (lang === 'ko' ? '주요' : 'Major')}
                </button>
              ))}
            </div>
            {/* Scope / Target */}
            <div className="flex gap-1.5">
              {(['all', 'holdings', 'watchlist'] as const).map(s => (
                <button
                  key={s}
                  onClick={() => setScopeFilter(s)}
                  className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all ${
                    scopeFilter === s
                      ? s === 'holdings' ? 'bg-purple-500/30 text-purple-300 border border-purple-500/40'
                        : s === 'watchlist' ? 'bg-blue-500/30 text-blue-300 border border-blue-500/40'
                        : 'bg-white text-black'
                      : 'bg-white/5 text-white/40 hover:text-white/70 border border-white/5'
                  }`}
                >
                  {s === 'all' ? (lang === 'ko' ? '전체 대상' : 'All Targets') :
                   s === 'holdings' ? (lang === 'ko' ? '보유 종목' : 'Holdings') :
                   (lang === 'ko' ? '관심 종목' : 'Watchlist')}
                </button>
              ))}
            </div>
          </div>

          {/* News list */}
          <div className="flex-1 overflow-y-auto space-y-2 pr-1.5 custom-scrollbar min-h-0">
            {isLoadingFeed && (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <div className={`w-8 h-8 border-2 ${theme.spinner} rounded-full animate-spin`} />
                <p className="text-[11px] text-white/30 font-medium">
                  {lang === 'ko' ? '뉴스를 불러오는 중...' : 'Loading news...'}
                </p>
              </div>
            )}
            {feedError && !isLoadingFeed && (
              <div className="flex flex-col items-center justify-center py-12 gap-3">
                <AlertTriangle className="w-8 h-8 text-amber-400/50" />
                <p className="text-[11px] text-white/40 text-center font-medium max-w-[220px]">
                  {lang === 'ko' ? '뉴스를 불러오지 못했습니다.' : 'Failed to load news.'}<br />
                  <button onClick={handleRefresh} className="text-amber-400 underline mt-1">
                     {lang === 'ko' ? '다시 시도' : 'Try again'}
                  </button>
                </p>
              </div>
            )}
            {!isLoadingFeed && !feedError && filteredNews.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 gap-3 text-white/30">
                <Newspaper className="w-8 h-8 opacity-30" />
                <p className="text-[11px] font-medium">
                  {lang === 'ko' ? '해당 조건의 뉴스가 없습니다.' : 'No news found for this filter.'}
                </p>
              </div>
            )}
            {filteredNews.map(item => {
                const isHolding = holdings.some(h => item.relatedSymbols.some(sym => sym.toUpperCase() === h.symbol.toUpperCase()));
                const isWatchlist = watchlist.some(w => item.relatedSymbols.some(sym => sym.toUpperCase() === w.symbol.toUpperCase()));
                
                // 1) Match by relatedSymbols ticker
                let matchedTrafficSig = item.relatedSymbols
                  .map(sym => trafficSignalsBySymbol[sym.toUpperCase().replace('.KS', '').replace('.KQ', '')])
                  .find(Boolean);

                // 2) If not matched, match by company name/keyword in news title & content
                if (!matchedTrafficSig) {
                  const text = `${item.title} ${item.content || ''}`.toLowerCase();
                  matchedTrafficSig = Object.values(trafficSignalsBySymbol).find(sig => {
                    const ko = (sig.companyName || '').toLowerCase();
                    const en = (sig.companyNameEn || '').toLowerCase();
                    const domain = (sig.primaryDomain || '').replace('.com', '').replace('.co.kr', '').toLowerCase();
                    return (
                      (ko.length >= 2 && text.includes(ko)) ||
                      (en.length >= 3 && text.includes(en)) ||
                      (domain.length >= 3 && text.includes(domain))
                    );
                  });
                }

                return (
                  <NewsCard
                    key={item.id}
                    item={item}
                    isSelected={selectedNews?.id === item.id}
                    onClick={() => handleSelectNews(item)}
                    lang={lang}
                    analysis={analyses[item.id]}
                    isHolding={isHolding}
                    isWatchlist={isWatchlist}
                    trafficSignal={matchedTrafficSig}
                  />
                );
              })}
          </div>
        </div>

        {/* ── RIGHT: Analysis Panel ── */}
        <div className={`
          ${selectedNews ? 'fixed inset-0 z-50 bg-[#050505] p-4 flex' : 'hidden'}
          md:relative md:inset-auto md:z-auto md:bg-transparent md:p-0 md:flex
          flex-1 flex-col h-full min-h-0 overflow-hidden
        `}>
          {!selectedNews ? (
            /* Empty state */
            <div className="flex-1 flex flex-col items-center justify-center gap-5 rounded-3xl border border-white/5 bg-white/[0.01] h-full">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500/20 to-orange-500/10 border border-amber-500/15 flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-amber-500/50" />
              </div>
              <div className="text-center">
                <p className="text-sm font-black text-white/30">
                  {lang === 'ko' ? '뉴스를 선택하세요' : 'Select a news item'}
                </p>
                <p className="text-[11px] text-white/20 font-medium mt-1">
                  {lang === 'ko'
                    ? '왼쪽에서 뉴스를 선택하면 포트폴리오 분석 결과가 노출됩니다'
                    : 'Select news from the left to view portfolio impact report'}
                </p>
              </div>
              <div className="flex items-center gap-2 text-[10px] text-white/20 font-medium">
                <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500/50" /> {lang === 'ko' ? '보유 종목' : 'Holdings'}</div>
                <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500/50" /> {lang === 'ko' ? '관심 종목' : 'Watchlist'}</div>
                <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500/50" /> {lang === 'ko' ? 'AI 전략 추천' : 'AI Strategy'}</div>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex flex-col h-full min-h-0 overflow-hidden">
              {/* Selected news top action bar */}
              <div className="glass-premium rounded-2xl border-white/5 px-4 py-3 mb-3 flex-shrink-0 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-md flex-shrink-0 ${URGENCY_STYLES[selectedNews.urgency]}`}>
                    {URGENCY_LABELS[selectedNews.urgency][lang]}
                  </span>
                  <span className="text-[10px] font-medium text-white/40 truncate">
                    {timeAgo(selectedNews.publishedAt, lang)} · {selectedNews.source}
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <a
                    href={selectedNews.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 px-2 py-1 rounded-lg bg-white/5 hover:bg-white/10 text-white/50 hover:text-white text-[11px] font-medium transition-all"
                    title={lang === 'ko' ? '원문 기사 보기' : 'View Source Article'}
                  >
                    <span>{lang === 'ko' ? '원문' : 'Source'}</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                  <button
                    onClick={() => { selectNews(null); }}
                    className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/40 hover:text-white transition-all"
                    title={lang === 'ko' ? '닫기' : 'Close'}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {/* Independent scrollable content area */}
              <div className="flex-1 overflow-y-auto custom-scrollbar space-y-4 pr-1.5 pb-6 min-h-0">
                {/* 1. Article Title & Summary Card */}
                <div className="glass-premium rounded-2xl border-white/5 p-5">
                  <h3 className="text-sm md:text-base font-bold text-white leading-snug">
                    {selectedNews.title}
                  </h3>

                  {(() => {
                    if (streamedSummaryText) {
                      return (
                        <div className="text-[11px] text-white/75 mt-3 leading-relaxed border-t border-white/5 pt-3 prose prose-invert max-w-none">
                          <ReactMarkdown 
                            remarkPlugins={[remarkGfm]} 
                            components={customMarkdownComponents}
                          >
                            {streamedSummaryText}
                          </ReactMarkdown>
                        </div>
                      );
                    }

                    if (isAnalyzing) {
                      return (
                        <div className="text-[11px] text-white/70 mt-3 leading-relaxed border-t border-white/5 pt-3 prose prose-invert max-w-none">
                          <span className="text-white/40 italic flex items-center gap-2 py-1">
                            <RefreshCw className="w-3.5 h-3.5 animate-spin text-emerald-400" />
                            {lang === 'ko' ? 'AI 실시간 요약 및 분석 실행 중...' : 'Running AI real-time summary & analysis...'}
                          </span>
                        </div>
                      );
                    }

                    const hasOriginalContent = selectedNews.content && selectedNews.content.trim().length >= 120;
                    if (hasOriginalContent) {
                      return (
                        <div className="text-[11px] text-white/75 mt-3 leading-relaxed border-t border-white/5 pt-3 prose prose-invert max-w-none">
                          <ReactMarkdown 
                            remarkPlugins={[remarkGfm]} 
                            components={customMarkdownComponents}
                          >
                            {selectedNews.content}
                          </ReactMarkdown>
                        </div>
                      );
                    }

                    return (
                      <div className="text-[11px] mt-3 border-t border-white/5 pt-3 space-y-2">
                        {selectedNews.content && selectedNews.content.trim().length > 0 && (
                          <div className="text-white/75 leading-relaxed prose prose-invert max-w-none">
                            <ReactMarkdown 
                              remarkPlugins={[remarkGfm]} 
                              components={customMarkdownComponents}
                            >
                              {selectedNews.content}
                            </ReactMarkdown>
                          </div>
                        )}
                        <div className="text-white/40 italic flex items-center gap-1.5">
                          <span>ℹ️</span>
                          <span>
                            {lang === 'ko'
                              ? 'AI 분석 실행 버튼을 클릭하면 실시간 뉴스 요약과 포트폴리오 영향도 분석이 함께 수행됩니다.'
                              : 'Click the Run AI Analysis button to generate a real-time summary and portfolio impact analysis.'}
                          </span>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Status indicators and action buttons */}
                  {isAnalyzing && (
                    <div className={`mt-4 flex items-center gap-2 text-[11px] ${theme.accent} font-medium border-t border-white/5 pt-3`}>
                      <div className={`w-3.5 h-3.5 border-2 ${theme.spinner} rounded-full animate-spin`} />
                      {lang === 'ko' ? '포트폴리오 영향도 분석 중...' : 'Analyzing portfolio impact...'}
                    </div>
                  )}

                  {(impactMap.length > 0 || strategyText) && !isAnalyzing && (
                    <div className="mt-4 border-t border-white/5 pt-3 flex items-center justify-between">
                      <button
                        onClick={handleAnalyze}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 rounded-xl text-white/60 hover:text-white text-[10px] font-bold uppercase tracking-wider transition-all border border-white/5"
                      >
                        <RefreshCw className="w-3 h-3" />
                        {lang === 'ko' ? '재분석' : 'Re-analyze'}
                      </button>
                      <span className="text-[10px] text-white/30 font-medium">
                        {lang === 'ko' ? 'AI 분석 완료' : 'AI Analysis Complete'}
                      </span>
                    </div>
                  )}
                </div>

                {/* CTA Card for unanalyzed state */}
                {!strategyText && !isAnalyzing && (
                  <div className="flex flex-col items-center justify-center p-8 text-center rounded-3xl border border-white/5 bg-white/[0.01] gap-4">
                    <div className="w-12 h-12 rounded-xl bg-purple-500/10 border border-purple-500/25 flex items-center justify-center">
                      <Sparkles className="w-6 h-6 text-purple-400 animate-pulse" />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-white">
                        {lang === 'ko' ? 'AI 포트폴리오 영향 분석' : 'AI Portfolio Impact Analysis'}
                      </h4>
                      <p className="text-[11px] text-white/40 mt-1 max-w-[320px] leading-relaxed">
                        {lang === 'ko'
                          ? '이 뉴스가 보유자산 및 관심종목의 비중, 수익률, 리스크에 미치는 영향을 AI로 정밀 분석합니다.'
                          : 'Analyze how this news impacts your holdings and watchlist weight, returns, and risk metrics.'}
                      </p>
                    </div>
                    <button
                      onClick={handleAnalyze}
                      className={`flex items-center gap-1.5 px-5 py-2.5 bg-gradient-to-r ${theme.gradient} text-black font-black text-xs rounded-xl shadow-lg transition-all transform hover:scale-[1.02]`}
                    >
                      <Play className="w-3.5 h-3.5 fill-black" />
                      {lang === 'ko' ? 'AI 분석 실행' : 'Run AI Analysis'}
                    </button>
                  </div>
                )}

                {/* 1.5 Web Search Enrichment Callout */}
                {(!selectedNews.content || selectedNews.content.length < 120) && strategyText && !isAnalyzing && (
                  <div className="p-4 rounded-2xl bg-purple-500/5 border border-purple-500/10 flex gap-3.5 items-center">
                    <div className="w-8 h-8 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400 text-sm">🔍</div>
                    <div>
                      <p className="text-[12px] font-bold text-purple-300">
                        {lang === 'ko' ? '실시간 웹 검색 맥락 반영됨' : 'Real-time Web Search Context Applied'}
                      </p>
                      <p className="text-[10px] text-white/40 mt-0.5 leading-normal">
                        {lang === 'ko'
                          ? '본 뉴스 기사의 원본 본문이 제공되지 않거나 짧아, AI가 실시간 웹 검색(Tavily)을 통해 수집한 최신 팩트와 맥락을 반영하여 종목 영향도를 정밀 분석했습니다.'
                          : 'Since the original news body is missing or short, the AI performed a precise analysis by incorporating the latest facts and context retrieved via real-time web search (Tavily).'}
                      </p>
                    </div>
                  </div>
                )}

                {/* 1. Negligible Impact Callout (UX Improvement) */}
                {(!isAnalyzing && impactMap.length === 0 && strategyText) && (
                  <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 flex gap-3.5 items-center">
                    <div className="w-8 h-8 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400 text-sm">⚖️</div>
                    <div>
                      <p className="text-[12px] font-bold text-white">
                        {lang === 'ko' ? '포트폴리오 직접 영향 미비' : 'Negligible Portfolio Impact'}
                      </p>
                      <p className="text-[10px] text-white/40 mt-0.5">
                        {lang === 'ko' 
                          ? '해당 뉴스는 보유/관심 종목에 유의미한 직접 영향도가 발견되지 않았습니다. 개괄적인 거시 포지션만 정리합니다.' 
                          : 'No significant direct impact found for your holdings/watchlist. Showing macro strategy.'}
                      </p>
                    </div>
                  </div>
                )}

                {/* 2. Impact Map (Only render if there are items) */}
                {(impactMap.length > 0 || (isAnalyzing && analysisText.includes('[IMPACT_MAP]'))) && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <BarChart2 className={`w-4 h-4 ${theme.accent}`} />
                      <h4 className="text-xs font-black uppercase tracking-widest text-white/60">
                        {lang === 'ko' ? '종목별 영향도 맵' : 'Symbol Impact Map'}
                      </h4>
                      <span className="text-[9px] text-white/25 font-medium ml-auto">
                        {lang === 'ko' ? '보유' : 'Held'} {impactMap.filter(e => e.isHolding).length} · {lang === 'ko' ? '관심' : 'Watch'} {impactMap.filter(e => e.isWatchlist).length}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[...impactMap]
                        .filter(entry => {
                          if (market === 'ALL') return true;
                          const isKrSymbol = entry.symbol.endsWith('.KS') || entry.symbol.endsWith('.KQ') || /^\d{6}$/.test(entry.symbol);
                          return market === 'KR' ? isKrSymbol : !isKrSymbol;
                        })
                        .sort((a, b) => {
                          if (a.isHolding !== b.isHolding) return a.isHolding ? -1 : 1;
                          if (a.isWatchlist !== b.isWatchlist) return a.isWatchlist ? -1 : 1;
                          return Math.abs(b.magnitude) - Math.abs(a.magnitude);
                        })
                        .map(entry => (
                          <div key={entry.symbol}>
                            <ImpactCard
                              entry={entry}
                              lang={lang}
                              onAddWatchlist={
                                onAddWatchlist
                                  ? (sym) => onAddWatchlist(sym, entry.name, market === 'KR' ? 'KR' : 'US', '')
                                  : undefined
                              }
                              onOpenSimulator={
                                onNavigateTo
                                  ? (sym, type) => onNavigateTo(type === 'holding' ? 'simulator' : 'portfolio', sym)
                                  : undefined
                              }
                            />
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* 3. Strategy report */}
                {strategyText && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className="w-4 h-4 text-purple-400" />
                      <h4 className="text-xs font-black uppercase tracking-widest text-white/60">
                        {lang === 'ko' ? 'AI 포지션 전략' : 'AI Position Strategy'}
                      </h4>
                    </div>
                    <div className="glass-premium rounded-2xl border border-white/5 p-6 shadow-xl relative overflow-hidden">
                      <ReactMarkdown 
                        remarkPlugins={[remarkGfm]} 
                        components={customMarkdownComponents}
                      >
                        {strategyText}
                      </ReactMarkdown>
                    </div>
                  </div>
                )}

                {/* Loading placeholder */}
                {isAnalyzing && !strategyText && impactMap.length === 0 && (
                  <div className="space-y-3">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="h-20 rounded-2xl bg-white/[0.03] animate-pulse border border-white/5" />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
